import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { normalizeStoreId, type PosStore } from "@/utils/posStores";

export interface NewStoreInput {
  brand: string;
  location_name: string;
  store_id: string;
}

export interface StoreEditInput {
  id: number;
  location_name: string;
  /** Omitted when the store already has a real Adora id: that id is immutable
   *  from the UI (pins and snapshots are keyed by it). */
  store_id?: string;
}

/** Postgres unique_violation, thrown here on a duplicate store_id (the
 *  partial unique index in 20260828120000_create_pos_stores.sql). */
const UNIQUE_VIOLATION = "23505";

function friendlyStoreError(error: { code?: string; message: string }): Error {
  if (error.code === UNIQUE_VIOLATION) {
    return new Error("That store ID is already in use by another store.");
  }
  return new Error(error.message);
}

/**
 * Inserts a new pos_stores row (captain's ask: add a store to an existing
 * brand, a new brand, or a new location) and invalidates the shared
 * ["pos-stores"] query so it appears immediately in search/pin/fetch.
 */
export function useAddStore() {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NewStoreInput): Promise<PosStore> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("pos_stores")
        .insert({
          brand: input.brand.trim(),
          location_name: input.location_name.trim(),
          store_id: normalizeStoreId(input.store_id),
        })
        .select("id, brand, store_id, location_name, available")
        .single();
      if (error) throw friendlyStoreError(error);
      return data as PosStore;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pos-stores"] });
    },
  });
}

/**
 * Updates a store's location name / Adora store id in place (brand is not
 * editable here; that's an add-a-new-store operation instead).
 */
export function useUpdateStore() {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StoreEditInput): Promise<PosStore> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("pos_stores")
        .update({
          location_name: input.location_name.trim(),
          ...(input.store_id !== undefined ? { store_id: normalizeStoreId(input.store_id) } : {}),
        })
        .eq("id", input.id)
        .select("id, brand, store_id, location_name, available")
        .single();
      if (error) throw friendlyStoreError(error);
      return data as PosStore;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pos-stores"] });
    },
  });
}
